import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail as MailIcon, Send } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    nom: "", prenom: "", email: "", telephone: "", type_bien: "", message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const validate = () => {
    const errs = {};
    if (!form.nom.trim()) errs.nom = "Requis";
    if (!form.prenom.trim()) errs.prenom = "Requis";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Email invalide";
    if (!form.telephone.trim() || !/^[+\d\s()-]{6,}$/.test(form.telephone)) errs.telephone = "Téléphone invalide";
    if (!form.type_bien) errs.type_bien = "Requis";
    if (!form.message.trim()) errs.message = "Requis";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm({ nom: "", prenom: "", email: "", telephone: "", type_bien: "", message: "" });
    } catch {
      setErrors({ submit: "Erreur lors de l'envoi. Veuillez réessayer." });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full bg-transparent border-b ${
      errors[field] ? "border-red-400" : "border-[#b39a73]/40"
    } py-3 px-1 text-[#4E4A45] text-lg placeholder:text-[#4E4A45]/40 focus:outline-none focus:border-[#b39a73] transition-colors`;

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#DED6CA]/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-6 mb-4">
            <div className="h-[1px] w-16 bg-[#b39a73]" />
            <h2
              className="text-3xl md:text-4xl text-[#2E3A34] tracking-[0.05em]"
              style={{ fontFamily: "'Cinzel', serif" }}
              data-testid="contact-title"
            >
              Demande de devis
            </h2>
            <div className="h-[1px] w-16 bg-[#b39a73]" />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="lg:col-span-3"
          >
            {success ? (
              <div data-testid="contact-success" className="bg-[#FAF8F5] border border-[#b39a73] p-10 text-center">
                <div className="w-16 h-16 rounded-full border border-[#b39a73] flex items-center justify-center mx-auto mb-6">
                  <Send className="h-7 w-7 text-[#b39a73]" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl text-[#2E3A34] mb-3" style={{ fontFamily: "'Cinzel', serif" }}>
                  Demande envoyée
                </h3>
                <p className="text-[#4E4A45] text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Merci pour votre confiance. Nous vous recontacterons dans les meilleurs délais.
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  data-testid="contact-new-request"
                  className="mt-6 border border-[#b39a73] text-[#4E4A45] px-6 py-2.5 text-sm tracking-[0.15em] uppercase hover:bg-[#b39a73] hover:text-white transition-all duration-300"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                >
                  Nouvelle demande
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div>
                    <input
                      data-testid="contact-nom"
                      type="text"
                      placeholder="Nom"
                      value={form.nom}
                      onChange={(e) => setForm({ ...form, nom: e.target.value })}
                      className={inputClass("nom")}
                    />
                    {errors.nom && <p className="text-red-400 text-sm mt-1">{errors.nom}</p>}
                  </div>
                  <div>
                    <input
                      data-testid="contact-prenom"
                      type="text"
                      placeholder="Prénom"
                      value={form.prenom}
                      onChange={(e) => setForm({ ...form, prenom: e.target.value })}
                      className={inputClass("prenom")}
                    />
                    {errors.prenom && <p className="text-red-400 text-sm mt-1">{errors.prenom}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div>
                    <input
                      data-testid="contact-email"
                      type="email"
                      placeholder="Email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={inputClass("email")}
                    />
                    {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <input
                      data-testid="contact-telephone"
                      type="tel"
                      placeholder="Téléphone"
                      value={form.telephone}
                      onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                      className={inputClass("telephone")}
                    />
                    {errors.telephone && <p className="text-red-400 text-sm mt-1">{errors.telephone}</p>}
                  </div>
                </div>

                <div>
                  <Select
                    value={form.type_bien}
                    onValueChange={(v) => setForm({ ...form, type_bien: v })}
                  >
                    <SelectTrigger
                      data-testid="contact-type-bien"
                      className={`w-full bg-transparent border-0 border-b ${
                        errors.type_bien ? "border-red-400" : "border-[#b39a73]/40"
                      } rounded-none py-3 px-1 text-lg focus:ring-0 focus:ring-offset-0 h-auto shadow-none`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      <SelectValue placeholder="Type de bien" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FAF8F5] border-[#b39a73]/30">
                      <SelectItem value="maison" className="text-[#4E4A45]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Maison</SelectItem>
                      <SelectItem value="villa" className="text-[#4E4A45]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Villa</SelectItem>
                      <SelectItem value="appartement" className="text-[#4E4A45]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Appartement</SelectItem>
                      <SelectItem value="autre" className="text-[#4E4A45]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Autre</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.type_bien && <p className="text-red-400 text-sm mt-1">{errors.type_bien}</p>}
                </div>

                <div>
                  <textarea
                    data-testid="contact-message"
                    placeholder="Votre message"
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className={`${inputClass("message")} resize-none`}
                  />
                  {errors.message && <p className="text-red-400 text-sm mt-1">{errors.message}</p>}
                </div>

                {errors.submit && <p className="text-red-400 text-sm">{errors.submit}</p>}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="bg-[#b39a73] text-white px-10 py-4 text-sm tracking-[0.2em] uppercase hover:bg-[#b8a68c] transition-all duration-300 disabled:opacity-50"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                >
                  {submitting ? "Envoi en cours..." : "Envoyer ma demande"}
                </button>

                <p className="text-sm text-[#4E4A45]/60 leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Vos données sont traitées conformément à notre politique de confidentialité et ne sont jamais cédées à des tiers.
                </p>
              </form>
            )}
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            className="lg:col-span-2"
          >
            <div id="coordonnees" className="bg-[#2E3A34] p-8 md:p-10 h-full">
              <h3
                className="text-xl text-white mb-8 tracking-[0.05em]"
                style={{ fontFamily: "'Cinzel', serif" }}
              >
                Nos coordonnées
              </h3>

              <div className="space-y-6">
                <a
                  href="mailto:aurahomecaretahiti@gmail.com"
                  data-testid="contact-email-link"
                  className="flex items-start gap-4 text-white/80 hover:text-[#b39a73] transition-colors group"
                >
                  <MailIcon className="h-5 w-5 text-[#b39a73] mt-1 shrink-0" strokeWidth={1.5} />
                  <span className="text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    aurahomecaretahiti@gmail.com
                  </span>
                </a>

                <a
                  href="tel:+68989510765"
                  data-testid="contact-phone-link"
                  className="flex items-start gap-4 text-white/80 hover:text-[#b39a73] transition-colors group"
                >
                  <Phone className="h-5 w-5 text-[#b39a73] mt-1 shrink-0" strokeWidth={1.5} />
                  <span className="text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    (+689) 89 51 07 65
                  </span>
                </a>

                <div className="flex items-start gap-4 text-white/80">
                  <MapPin className="h-5 w-5 text-[#b39a73] mt-1 shrink-0" strokeWidth={1.5} />
                  <span className="text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Punaauia, Tahiti — Polynésie française
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
